import { Routes, RouteName } from '@/router/routes'
import { TNKeys } from '@/components/lang/langHandler'
import type { useUserStore } from '@/stores/userStore'
import {
  adminNavigationItems,
  salesNavigationItems
} from '@/views/OrganisationDetails/composables/useAdminNavigation'

type UserStore = ReturnType<typeof useUserStore>

export interface NavItems {
  label: TNKeys
  route?: RouteName | null
  subroutes?: NavItems[]
  type?: string
  betaOnly?: boolean
  newsboxFeatureOnly?: boolean
}

type SideBarStructure = {
  primary: NavItems[]
  secondary: NavItems[]
  admin: NavItems[]
}

type DropdownStructure = {
  group1: NavItems[]
  group2?: NavItems[]
  group3?: NavItems[]
}

const adminSubroutes: NavItems[] = adminNavigationItems.map(item => ({
  label: item.labelKey,
  route: item.route
}))

const salesSubroutes: NavItems[] = salesNavigationItems.map(item => ({
  label: item.labelKey,
  route: item.route
}))

function getAdminSubroutes(userStore: UserStore): NavItems[] {
  if (userStore.isAdmin) {
    return [
      ...adminSubroutes,
      { label: 'chiefEditor', route: Routes.CHIEF_EDITOR },
      { label: 'prompts', route: Routes.PROMPTS },
      { label: 'formatBuilder', route: Routes.ADMIN_FORMAT_BUILDER }
    ]
  }
  if (userStore.isSales) {
    return [
      ...salesSubroutes,
      { label: 'chiefEditor', route: Routes.CHIEF_EDITOR },
      { label: 'formatBuilder', route: Routes.ADMIN_FORMAT_BUILDER }
    ]
  }
  return []
}

const betaPrimaryItems: NavItems[] = [
  { label: 'imageStudio', route: Routes.IMAGE_STUDIO, betaOnly: true },
  { label: 'heartbeat', route: Routes.HEARTBEAT, betaOnly: true }
]

export const getSidebarItems = (userStore: UserStore): SideBarStructure => ({
  primary: [
    { label: 'home', route: Routes.HOME },
    { label: 'editor', route: Routes.EDITOR },
    { label: 'assistant', route: Routes.ASSISTANT },
    ...betaPrimaryItems,
    { label: 'newsbox', route: Routes.NEWSBOX, newsboxFeatureOnly: true }
  ],
  secondary: [
    { label: 'library', route: Routes.REWARDS },
    { label: 'liveContent', route: Routes.CONTENT },
    { label: 'startVideo', route: Routes.TUTORIAL }
  ],
  admin: [
    ...(userStore.isSales
      ? [
          {
            label: 'adminDashboard' as const,
            subroutes: getAdminSubroutes(userStore)
          }
        ]
      : []),
    ...(userStore.isOrgaAdmin
      ? [
          {
            label: 'settings' as const,
            subroutes: [
              { label: 'organisationSettings' as const, route: Routes.PROFILE_ORGANISATION },
              ...(userStore.isSuperOrgaAdmin
                ? [
                    {
                      label: 'managedOrganisationsSettings' as const,
                      route: Routes.PROFILE_SUPER_ORGANISATION
                    }
                  ]
                : []),
              { label: 'formatBuilder' as const, route: Routes.PROFILE_FORMAT_BUILDER },
              { label: 'styleSettings' as const, route: Routes.PROFILE_STYLE_SETTINGS },
              { label: 'languageSettings' as const, route: Routes.PROFILE_LANGUAGE_SETTINGS },
              { label: 'contentDashboard' as const, route: Routes.PROFILE_PRODUCTIVITY }
            ]
          }
        ]
      : [])
  ]
})

export const getDropdownItems = (userStore: UserStore): DropdownStructure => ({
  group1: [
    { label: 'imprint', route: Routes.IMPRINT },
    { label: 'privacy', route: Routes.PRIVACY },
    { label: 'tos', route: Routes.TOS }
  ],
  group2: userStore.isOrgaAdmin
    ? [{ label: 'billingOverview', route: Routes.PROFILE_ORGANISATION }]
    : undefined,
  group3: userStore.isSales ? [{ label: 'adminDashboard', route: Routes.ADMIN_USERS }] : undefined
})
